import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  ExportButton,
  ReferenceField,
  FunctionField,
} from 'react-admin';

import useDefaultFilter from '@/utils/useDefaultFilter';
import { Can, ACCESS, useAuth } from '@/utils/auth';
import { USER, TRANSACTION, MACHINE } from '@/vars';
import { TRANSACTIONS } from '@/vars/resources';
import routes from '@/vars/routes';
import { Page } from '@/components';
import TransactionFilter from '../TransactionFilter';
import Toolbar from '@material-ui/core/Toolbar';
import { cloneDeep, get } from 'lodash';
import {
  checkMachineDualMode,
  getEmployeeDocs,
  getProductDocs,
  getProductCategoryDocs,
  renderDateMY,
  renderTimeMY,
  getPaymentType,
  getEmployee,
  getProduct,
  getProductCategory,
  getFormattedSlotId,
  getStatus,
  getErrorCategoryMessage,
  setData,
  exportCSV,
  renderTransactionStatus,
} from '@/utils/transaction';
import { Button, Modal } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CustomizedDialogs from '../TransactionDetails';
import { mroGeneralTemplate, mroMachineDualModeTemplate } from '@/vars/templates';

const MROTransactionList = ({ staticContext, ...props }) => {
  const { permissions } = useAuth();
  const isAdmin = permissions.role === USER.ROLE.ADMIN;
  const clientId = permissions.client_id;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');

  const classes = useStyles();

  var filter = {
    ...useDefaultFilter('client'),
    mode: MACHINE.MODE.MRO,
  };
  if (!isAdmin) filter.status = TRANSACTION.STATUS.SUCCESS;

  const listProps = {
    ...props,
    basePath: routes.mroTransactions,
    resource: TRANSACTIONS,
    hasCreate: false,
    hasEdit: false,
    hasShow: false,
    hasList: true,

    filter,
    sort: { field: 'date', order: 'DESC' },
    bulkActionButtons: false,
    perPage: 25,
    filters: <TransactionFilter />,
  };

  const PostActions = ({ currentSort, displayedFilters, filters, filterValues, resource, showFilter, total }) => {
    filter.status = filterValues.status;
    return (
      <Toolbar>
        {filters &&
          React.cloneElement(filters, {
            resource,
            showFilter,
            displayedFilters,
            filterValues,
            context: 'button',
          })}
        <ExportButton
          disabled={total === 0}
          maxResults={total}
          resource={TRANSACTIONS}
          sort={currentSort}
          filter={{ ...filterValues, ...filter }}
          exporter={exporter}
        />
      </Toolbar>
    );
  };

  const exporter = async posts => {
    const datasetCopy = cloneDeep(posts);
    try {
      if (isAdmin) {
        let datas = await Promise.all(
          //Promise.all resolve when all promises are resolved
          datasetCopy.map(async post => {
            post.time = renderTimeMY(post);
            post.date = renderDateMY(post);

            return post;
          })
        );
        exportCSV(datas, null);
        return;
      }
      
      // client template
      const isMachineDualMode = await checkMachineDualMode(clientId);

      const employees = await getEmployeeDocs(clientId);
      const products = await getProductDocs(clientId);
      const categories = await getProductCategoryDocs(clientId);

      let datas = await Promise.all(
        //Promise.all resolve when all promises are resolved
        datasetCopy.map(async post => {
          post.time = renderTimeMY(post);
          post.date = renderDateMY(post);

          const { department, cardNumber } = getEmployee(post, employees);
          setData(post, 'employee.department', department);
          setData(post, 'employee.cardNumber', cardNumber);

          const { sku, serialNumber, priceCash, priceCredit } = getProduct(post, products);
          setData(post, 'product.priceCash', priceCash);
          setData(post, 'product.priceCredit', priceCredit);
          setData(post, 'product.sku', sku);
          setData(post, 'product.serialNumber', serialNumber);

          const formattedSlotId = getFormattedSlotId(post);
          setData(post, 'product.formattedSlotId', formattedSlotId);

          const { category } = getProductCategory(post, categories);
          setData(post, 'product.categoryName', category);

          if (get(post, 'payment.gatewayID')) {
            setData(post, 'payment.paymentGateway', 'Ipay88');
            setData(post, 'payment.ewalletType', getPaymentType(post));
          } else {
            setData(post, 'payment.paymentGateway', getPaymentType(post));
          }
          setData(post, 'status', getStatus(post));

          const { category: errorCategory, message } = getErrorCategoryMessage(post);
          setData(post, 'error.message', message);

          return post;
        })
      );

      if (isMachineDualMode == true) exportCSV(datas, mroMachineDualModeTemplate);
      else exportCSV(datas, mroGeneralTemplate);
    } catch (e) {
      alert(e);
    }
  };

  const renderAdminMoreButtonField = record => {
    return (
      <Button
        size="medium"
        onClick={() => {
          console.log(isAdmin);
          setModalOpen(true);
          setModalContent(JSON.stringify(record, undefined, 3));
        }}
      >
        &gt;
      </Button>
    );
  };

  const renderCustomerMoreButtonField = record => {
    return <CustomizedDialogs {...record} />;
  };

  return (
    <Can module={ACCESS.MRO_REPORT}>
      <Page sectionTitle="MRO Report" title="List transactions">
        <div
          style={{
            display: 'flex',
            flex: '1 1 auto',
            overflowY: 'hidden',
            overflowX: 'scroll',
          }}
        >
          <List {...listProps} actions={<PostActions />}>
            <Datagrid>
              <DateField label="Date/Time" source="date" showTime />
              {isAdmin && (
                <ReferenceField label="Company" source="client" reference="clients" link={false}>
                  <TextField source="companyName" />
                </ReferenceField>
              )}
              <TextField label="Machine UUID" source="machine.machineUUID" />
              <TextField label="Employee Name" source="employee.fullName" />
              <ReferenceField label="Department" source="employee.id" reference="employees" link={false}>
                <TextField source="type.name"></TextField>
              </ReferenceField>
              <TextField label="Product name" source="product.name" />
              <NumberField source="amount" label="Price(Cr/RM)" />
              <FunctionField label="Status" render={renderTransactionStatus} />
              {isAdmin ? (
                <FunctionField label="" render={renderAdminMoreButtonField} />
              ) : (
                <FunctionField label="" render={renderCustomerMoreButtonField} />
              )}
            </Datagrid>
          </List>
          <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
            <div className={classes.paper}>
              <pre>{modalContent}</pre>
            </div>
          </Modal>
        </div>
      </Page>
    </Can>
  );
};

const useStyles = makeStyles(theme => ({
  paper: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 500,
    maxHeight: '90%',
    overflow: 'scroll',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

export default MROTransactionList;
